import { computed, inject, Injectable, OnDestroy } from '@angular/core';
import {
	ActivatedRoute,
	ActivatedRouteSnapshot,
	ActivationEnd,
	NavigationExtras,
	Params,
	QueryParamsHandling,
	Router,
	UrlTree,
} from '@angular/router';
import { isNil, last, pick } from 'lodash-es';
import { Selection } from '../shared/dashboard/base-dashboard/base-dashboard-config';
import {
	catchError,
	combineLatestWith,
	debounceTime,
	distinctUntilChanged,
	EMPTY,
	filter,
	ReplaySubject,
	Subscription,
	switchMap,
	withLatestFrom,
} from 'rxjs';
import { QueryParamStateService } from './query-param-state.service';
import { PageStateService, PsName } from './page-state.service';
import { FilterExpression, flattenFilters } from './data.service';
import { AutorisatieService } from './autorisatie.service';
import { CurrentUrlService, RestService } from '@cumlaude/shared-services';
import { UserRouteData } from './user-routes.service';
import { UserService } from './user.service';

interface RouteTreeNiveau {
	title: string;
	name: string;
}

export interface RouteData extends RouteTreeNiveau {
	from: string;
	path: string;
}

@Injectable({
	providedIn: 'root',
})
export class UrlService implements OnDestroy {
	private readonly activatedRoute = inject(ActivatedRoute);
	private readonly autorisatieService = inject(AutorisatieService);
	readonly currentUrlService = inject(CurrentUrlService);
	readonly router = inject(Router);

	private readonly subscriptions: Subscription[] = [];
	private readonly routeTree$ = new ReplaySubject<RouteTreeNiveau[]>(1);
	routeData$ = new ReplaySubject<RouteData>(1);

	readonly isBeheer = computed(() => {
		const currentUrl = this.currentUrlService.currentUrl();
		return currentUrl?.substring(1).startsWith('beheer') ?? false;
	});

	constructor() {
		const qp = inject(QueryParamStateService);
		const ps = inject(PageStateService);
		const restService = inject(RestService);
		const userService = inject(UserService);

		this.subscriptions.push(
			this.router.events
				.pipe(filter((event) => event instanceof ActivationEnd))
				.subscribe((event) => this.routeTree$.next(this.getRouteTree(event.snapshot.root))),
			this.routeTree$
				.pipe(combineLatestWith(qp.observe('variant'), ps.observe(PsName.prognose), ps.observe(PsName.subject)))
				.subscribe(([routeTree, variant, prognose, subject]) => this.updateRouteData(routeTree, variant, prognose === 'true', subject)),

			// Houdt de recente pagina's bij: elke navigatie (incl. filter-/groeperingswijzigingen op
			// hetzelfde dashboard) wordt server-side aangemaakt of bijgewerkt (dedupe op basis-URL +
			// actieve rol). Zonder actieve rol (bijv. vóór de eerste rolkeuze) wordt niets gepost.
			this.currentUrlService.currentUrl$
				.pipe(
					debounceTime(300),
					distinctUntilChanged(),
					withLatestFrom(this.routeData$),
					filter(([, routeData]) => !!routeData.name),
					switchMap(([url, routeData]) => {
						const rol = userService.getActiveRol();
						if (isNil(rol)) return EMPTY;

						return restService.postRecentePagina({ url, naam: routeData.name, rol }).pipe(catchError(() => EMPTY));
					})
				)
				.subscribe()
		);
	}

	private updateRouteData(routeTree: RouteTreeNiveau[], variant?: string, prognose?: boolean, subject?: string) {
		let titles = routeTree.map((niveau) => niveau.title);
		if (subject) titles = titles.map((value) => value.replace('%subject%', subject));

		let path = titles.join(' - ');
		if (prognose) path = path.replace('%prognose%', 'Prognose');
		else path = path.replace('%prognose%', '').trim();

		if (variant) path += ` - ${variant}`;

		const from = titles[0] === 'Details' ? titles[1] : titles[0];

		const title = titles
			.at(-1)
			?.replace('%prognose%', prognose ? 'Prognose' : '')
			.trim();

		const name = routeTree.at(-1)?.name;

		const routeData = <RouteData>{
			title,
			name,
			from,
			path,
		};
		this.routeData$.next(routeData);
	}

	getFrom(): string {
		const root = this.activatedRoute.snapshot.root;
		const tree = this.getRouteTree(root);
		return tree[0].title === 'Details' ? tree[1].title : tree[0].title;
	}

	private getRouteTree(route: ActivatedRouteSnapshot): RouteTreeNiveau[] {
		const firstChild = route.firstChild;
		const childTree = isNil(firstChild) ? [] : this.getRouteTree(firstChild);
		const routeData = route.data as UserRouteData;
		const name = routeData?.name;

		if (isNil(name)) return childTree;

		return [{ title: routeData?.title ?? name, name }, ...childTree];
	}

	getRouterLink(selection?: Selection): string | undefined {
		if (!selection) return undefined;

		const urlRequested = selection.detailUrl;
		if (!urlRequested) return undefined;

		const urlAllowed = this.autorisatieService.checkUrlForActiveRol(urlRequested);
		if (urlAllowed === true) return urlRequested;
		if (urlAllowed === false) return undefined;
		return urlAllowed.toString();
	}

	navigateToSelection(selection?: Selection, overrideSchooljaar?: string) {
		const urlTree = this.getSelectionUrlTree(selection, overrideSchooljaar);
		if (urlTree) this.router.navigateByUrl(urlTree, { state: { doorklik: true } });
	}

	getSelectionUrlTree(selection?: Selection, overrideSchooljaar?: string): UrlTree | undefined {
		if (!selection) return undefined;

		const { detailUrl, mergeQueryParams } = selection;
		const currentQueryParams = mergeQueryParams ? this.activatedRoute.snapshot.queryParams : {};

		const urlAllowed = this.autorisatieService.checkUrlForActiveRol(detailUrl);
		if (urlAllowed === false) return undefined;

		const urlTree = urlAllowed === true ? this.router.parseUrl(detailUrl) : urlAllowed;
		const queryParams = { ...currentQueryParams, ...urlTree.queryParams, ...this.getSelectionQueryParams(selection, overrideSchooljaar) };

		return Object.assign(urlTree, { queryParams });
	}

	getRedirectUrlTree(commands: string[], queryParams: Params): UrlTree | undefined {
		const url = this.router.serializeUrl(this.router.createUrlTree(commands, { queryParamsHandling: 'merge' }));

		const urlAllowed = this.autorisatieService.checkUrlForActiveRol(url);
		if (urlAllowed === false) return undefined;
		const urlTree = urlAllowed === true ? this.router.parseUrl(url) : urlAllowed;

		return Object.assign(urlTree, { queryParams: { ...urlTree.queryParams, ...queryParams } });
	}

	getSelectionQueryParams(selection?: Selection, overrideSchooljaar?: string) {
		if (!selection) return;

		const from = this.getFrom();
		const schooljaar = overrideSchooljaar ?? this.getSchooljaarFromFilter(selection.expression);
		const docent = selection.detailUrl.startsWith('/details/docent');
		const adhoc = {
			...pick(selection, 'expression', 'size'),
			naam: `${docent ? 'Docentselectie' : 'Leerlingselectie'} - ${from} (${selection.size})`,
		};

		return { [docent ? 'adhoc-docent' : 'adhoc']: JSON.stringify(adhoc), ...(schooljaar ? { schooljaar } : {}), from };
	}

	private getSchooljaarFromFilter(filter?: FilterExpression | FilterExpression[]): string | undefined {
		if (!filter) return undefined;

		const filters = flattenFilters(filter);
		let schooljaar = last(filters.filter((filter) => filter.attr.join('.').includes('nm_schooljaar_van')));
		if (!schooljaar) schooljaar = last(filters.filter((filter) => filter.attr.join('.').includes('nm_schooljaar')));

		if (!schooljaar) return undefined;

		const value = schooljaar.val;
		return Array.isArray(value) ? last(value) : value;
	}

	redirect(url: string[], queryParams: Params, queryParamsHandling: QueryParamsHandling = 'merge', extras: NavigationExtras = {}) {
		return this.router.navigate(url, { ...extras, queryParams, queryParamsHandling });
	}

	redirectDoorklik(url: string[], queryParams: Params, queryParamsHandling: QueryParamsHandling = 'merge') {
		return this.redirect(url, queryParams, queryParamsHandling, { state: { doorklik: true } });
	}

	ngOnDestroy() {
		this.subscriptions.forEach((sub) => sub.unsubscribe());
	}
}
